<script setup lang="ts">
const windowScroll = useWindowScroll();
const defaultsStore = useDefaultsStore();
const header = useHeader();

const props = defineProps<{ 
    topics: {
        id: string,
        title: string
    }[],
}>();
const containerRef = useTemplateRef('containerRef');
const parentRef = useTemplateRef('parentRef');
const tabsRef = useTemplateRef('tabsRef');
const indicatorRef = useTemplateRef('indicatorRef');
const offset = useElementBounding(containerRef);
const parentRefSize = useElementSize(parentRef);
const activeTopic = ref<TTopics[number]>();
const isClickScrolling = ref(false);
const isOpen = ref(false);

export type TTopics = typeof props.topics;

const isSticky = computed(() => {
    if (!windowScroll.y || !offset.top.value) return;

    if (header.isHidden.value) return offset.top.value <= 0;
    return offset.top.value <= defaultsStore.headerHeight;
});


const debounce = useDebounceFn(() => isClickScrolling.value = false, 1000);
function scrollTo(id: string) {
    isClickScrolling.value = true;
    const elem = document.getElementById(id);
    elem?.scrollIntoView({ behavior: 'smooth' });
    debounce();
};

function activateTopic(topic: TTopics[number], index: number) {
    activeTopic.value = topic;
    moveActiveIndicator(parentRef, indicatorRef, tabsRef, index);
};

function handleOnClickTab(topic: TTopics[number], index: number) {
    activateTopic(topic, index);
    scrollTo(topic.id);
    isOpen.value = false;
};

function handleOpenTopics() {
    isOpen.value = !isOpen.value;
};


onMounted(async () => {
    if (!props.topics?.length || !tabsRef.value?.length) return;

    defaultsStore.tocHeight = parentRefSize.height.value;
    activeTopic.value = props.topics[0];
    props.topics.forEach((topic, index) => {
        const el = document.getElementById(topic.id);
        if (!el) return;

        useIntersectionObserver(
            el,
            ([entry]) => {
                if (!entry?.isIntersecting || isClickScrolling.value) return;
                activateTopic(topic, index);
            },
            { rootMargin: '-40% 0px -40% 0px' }
        )
    });

    await nextTick();
    moveActiveIndicator(parentRef, indicatorRef, tabsRef, 0);
});
</script>


<template>
    <SectionsBase>
        <!-- this div is for filling in the blanks -->
        <div 
            ref="containerRef" 
            class="w-full" 
            :style="{ height: isSticky ? `${ defaultsStore.tocHeight }px` : '0' }"
        />
        <!---->
        <div 
            class="w-full lg:border-t lg:border-b lg:border-gray-200" 
            :class="isSticky && 'fixed left-0 top-0 z-39 transition-transform duration-300'"
            :style="{ 
                transform: !isSticky || header.isHidden.value
                    ? 'translateY(0)' 
                    : `translateY(${ defaultsStore.headerHeight }px)` 
            }"
        >
            <div 
                ref="parentRef" 
                class="w-full bg-white relative items-center border-r border-l border-b border-gray-200
                    rounded-b-md lg:justify-between lg:shadow-none lg:rounded-none lg:border-r-0 lg:border-l-0"
            >
                <DevicesDesktop>
                    <svg ref="indicatorRef" class="w-0 h-0.5 bg-primary-600 absolute -bottom-0.5 transition duration-500"></svg>
                </DevicesDesktop>
                
                <DevicesMobile>
                    <div 
                        class="w-full p-4 rounded-md flex-center gap-3 text-primary-600 select-none shadow-primary-xs"
                        :class="[ isOpen && 'blur-bg', !isSticky && 'container' ]"
                        @click="handleOpenTopics"
                    >
                        <span class="lg:hidden">{{ activeTopic?.title }}</span>
                        <Icon 
                            name="solar:alt-arrow-down-linear" 
                            class="transition-transform duration-300"
                            :class="isOpen && '-rotate-180'" 
                        />
                    </div>
                </DevicesMobile>
                
                <div 
                    class="w-full grid lg:grid-rows-[1fr]! transition-all duration-300 [&>div]:overflow-hidden 
                        shadow-gray-xs lg:shadow-none container"
                    :class="isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
                >
                    <div class="w-full flex flex-col items-center lg:flex-row lg:justify-between">
                        <span
                            ref="tabsRef"
                            v-for="topic, index in topics"
                            :key="topic.id"
                            class="text-body2 text-gray-500 cursor-pointer transition-colors duration-300 
                                h-full flex items-center lg:px-0.5 2xl:px-4 py-2 lg:py-4 select-none"
                            :class="activeTopic?.id === topic.id && 'lg:text-primary-600 lg:bg-linear-to-b from-transparent to-primary-100 lg:font-semibold!'"
                            @click="handleOnClickTab(topic, index)"
                        >
                            {{ topic.title }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </SectionsBase>
</template>

<style scoped>
@reference "~/assets/css/main.css";

.blur-bg::before {
    @apply
    content-[''] w-screen h-screen backdrop-blur-sm z-[-1] fixed left-0 top-0
}
</style>